import React from "react";
import styles from './KycPolicy.module.css';

const KycPolicy = () => {
  const lastUpdated = "November 12, 2025";

  return (
    <div className={styles.kycPolicyContainer}>
      <main className={styles.kycContent}>
        <div className={styles.importantNotice}>
          <h1>Know Your Customer (KYC) & Anti-Money Laundering (AML) Policy</h1>
          <p className={styles.noticeText}>
            Technology-Enabled Peer-to-Peer Lending Platform
          </p>
          
          <p className={styles.lastUpdated}>Last Updated: {lastUpdated} | Version 1.0</p>
        </div>

        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>1</span>
            Policy Preamble & Objectives
          </h2>
          
          <div className={styles.sectionContent}>
            <h3>1.1 Purpose Statement</h3>
            <p>
              We are committed to operating a secure, transparent, and legally compliant 
              peer-to-peer lending platform. This KYC & AML Policy establishes our 
              framework for customer identification, verification, risk assessment, 
              and ongoing monitoring in accordance with Indian regulatory requirements.
            </p>

            <h3>1.2 Primary Objectives</h3>
            <div className={styles.objectivesList}>
              <div className={styles.objectiveItem}>
                <h4>Prevent Financial Crimes</h4>
                <p>Mitigate risks of money laundering, terrorist financing, fraud, and identity theft</p>
              </div>
              <div className={styles.objectiveItem}>
                <h4>Regulatory Compliance</h4>
                <p>Adhere to RBI guidelines, PMLA 2002, and other applicable regulations</p>
              </div>
              <div className={styles.objectiveItem}>
                <h4>Platform Integrity</h4>
                <p>Maintain trust and security for all lenders and borrowers</p>
              </div>
              <div className={styles.objectiveItem}>
                <h4>User Protection</h4>
                <p>Safeguard users from unauthorized access and financial misconduct</p>
              </div>
            </div>
          </div>
        </section>

        {/* Scope & Applicability */}
        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>2</span>
            Scope & Applicability
          </h2>

          <div className={styles.sectionContent}>
            <h3>2.1 Covered Entities</h3>
            <ul className={styles.coveredList}>
              <li><strong>Individual Users:</strong> All individual lenders and borrowers registered on the platform</li>
              <li><strong>Business Profiles:</strong> MSMEs and business entities using lending services</li>
              <li><strong>Co-Borrowers & Guarantors:</strong> All parties involved in loan agreements</li>
              <li><strong>Platform Infrastructure:</strong> All systems, processes, and third-party integrations</li>
            </ul>

            <div className={styles.noticeBox}>
              <h3>2.2 Platform Model Disclosure</h3>
              <p><strong>Important Notice: Platform Role</strong></p>
              <p>
                <strong>We operate solely as a technology intermediary</strong> facilitating 
                connections between lenders and borrowers. We do not:
              </p>
              <ul>
                <li>Provide credit guarantees or repayment assurances</li>
                <li>Act as a financial institution or bank</li>
                <li>Underwrite loans or assume credit risk</li>
                <li>Guarantee investment returns to lenders</li>
              </ul>
              <p className={styles.noticeNote}>
                Lenders independently assess borrowers and assume credit risk. 
                Borrowers remain solely responsible for repayment obligations.
              </p>
            </div>
          </div>
        </section>

        {/* Verification Framework */}
        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>3</span>
            Customer Identification & Verification Framework
          </h2>

          <div className={styles.sectionContent}>
            <h3>3.1 Multi-Tier Verification System</h3>
            
            <div className={styles.tierSystem}>
              <div className={styles.tier}>
                <div className={styles.tierHeader}>
                  <span className={styles.tierLabel}>Tier 1: Essential Verification</span>
                  <span className={styles.tierTag}>Mandatory for All</span>
                </div>
                <ul className={styles.tierRequirements}>
                  <li>PAN Card Verification</li>
                  <li>Aadhaar Offline XML/QR</li>
                  <li>Bank Account Validation</li>
                  <li>Live Photograph/Selfie</li>
                </ul>
              </div>

              <div className={styles.tier}>
                <div className={styles.tierHeader}>
                  <span className={styles.tierLabel}>Tier 2: Enhanced Verification</span>
                  <span className={styles.conditionalTag}>Conditional Application</span>
                </div>
                <ul className={styles.tierRequirements}>
                  <li>Video KYC Verification</li>
                  <li>Income Proof Submission</li>
                  <li>Physical Address Verification</li>
                  <li>Credit Bureau Checks</li>
                </ul>
                <p className={styles.tierNote}><strong>Triggers:</strong> High-value transactions, suspicious patterns, regulatory requirements</p>
              </div>
            </div>

            <h3>3.2 Acceptable Documents for Verification</h3>
            
            <div className={styles.documentsSection}>
              <div className={styles.documentCategory}>
                <h4>Identity Proof (Any One)</h4>
                <ul className={styles.documentsList}>
                  <li><strong>PAN Card</strong> <span className={styles.mandatory}>Mandatory</span></li>
                  <li><strong>Aadhaar Card</strong> <span className={styles.mandatory}>Mandatory</span></li>
                  <li><strong>Passport</strong> <span className={styles.optional}>Optional</span></li>
                  <li><strong>Driver's License</strong> <span className={styles.optional}>Optional</span></li>
                </ul>
              </div>

              <div className={styles.documentCategory}>
                <h4>Address Proof (Any One)</h4>
                <ul className={styles.documentsList}>
                  <li><strong>Aadhaar Card</strong> <span className={styles.preferred}>Preferred</span></li>
                  <li><strong>Utility Bill</strong> <span className={styles.accepted}>Accepted</span></li>
                  <li><strong>Bank Statement</strong> <span className={styles.accepted}>Accepted</span></li>
                  <li><strong>Rental Agreement</strong> <span className={styles.accepted}>Accepted</span></li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Risk Assessment */}
        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>4</span>
            Risk-Based Assessment Framework
          </h2>

          <div className={styles.sectionContent}>
            <h3>4.1 Risk Classification Criteria</h3>
            
            <div className={styles.riskLevels}>
              <div className={styles.riskLevel}>
                <div className={styles.riskHeader}>
                  <div className={`${styles.riskIndicator} ${styles.low}`}></div>
                  <h4>Low Risk</h4>
                </div>
                <ul>
                  <li>Salaried individuals with stable income</li>
                  <li>Resident Indians with verified address</li>
                  <li>Transaction amounts below ₹50,000</li>
                  <li>Clear source of funds</li>
                </ul>
                <p><strong>Monitoring:</strong> Standard periodic review</p>
              </div>

              <div className={styles.riskLevel}>
                <div className={styles.riskHeader}>
                  <div className={`${styles.riskIndicator} ${styles.medium}`}></div>
                  <h4>Medium Risk</h4>
                </div>
                <ul>
                  <li>Self-employed professionals</li>
                  <li>Transactions ₹50,000 - ₹2,00,000</li>
                  <li>Frequent lending/borrowing activity</li>
                  <li>Multiple bank accounts</li>
                </ul>
                <p><strong>Monitoring:</strong> Enhanced scrutiny, quarterly review</p>
              </div>

              <div className={styles.riskLevel}>
                <div className={styles.riskHeader}>
                  <div className={`${styles.riskIndicator} ${styles.high}`}></div>
                  <h4>High Risk</h4>
                </div>
                <ul>
                  <li>Politically Exposed Persons (PEPs)</li>
                  <li>Transactions above ₹2,00,000</li>
                  <li>Cross-border elements</li>
                  <li>Unclear source of funds</li>
                </ul>
                <p><strong>Monitoring:</strong> Continuous surveillance, EDD required</p>
              </div>
            </div>

            <h3>4.2 Enhanced Due Diligence (EDD)</h3>
            <div className={styles.eddProcess}>
              <div className={styles.eddStep}>
                <h5>1. Additional Documentation</h5>
                <p>Income proofs, business registration, source of wealth documents</p>
              </div>
              <div className={styles.eddStep}>
                <h5>2. Senior Management Approval</h5>
                <p>Mandatory approval for high-risk customer onboarding</p>
              </div>
              <div className={styles.eddStep}>
                <h5>3. Ongoing Monitoring</h5>
                <p>Monthly transaction reviews and pattern analysis</p>
              </div>
            </div>
          </div>
        </section>

        {/* AML & Monitoring */}
        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>5</span>
            AML & Transaction Monitoring Procedures
          </h2>

          <div className={styles.sectionContent}>
            <h3>5.1 Monitoring Systems</h3>
            
            <div className={styles.monitoringMethods}>
              <div className={styles.method}>
                <h4>Automated Monitoring</h4>
                <ul>
                  <li>Real-time transaction pattern analysis</li>
                  <li>Anomaly detection algorithms</li>
                  <li>Geographic risk assessment</li>
                  <li>Velocity checking (frequency analysis)</li>
                </ul>
              </div>
              
              <div className={styles.method}>
                <h4>Manual Oversight</h4>
                <ul>
                  <li>Compliance team reviews</li>
                  <li>Suspicious activity investigation</li>
                  <li>Periodic account audits</li>
                  <li>Customer behavior analysis</li>
                </ul>
              </div>
            </div>

            <h3>5.2 Red Flags & Alert Triggers</h3>
            <div className={styles.redFlags}>
              <div className={styles.redFlag}>
                <h5>Transaction Patterns</h5>
                <p>Structuring, round-figure transactions, rapid fund movement</p>
              </div>
              <div className={styles.redFlag}>
                <h5>Document Issues</h5>
                <p>Forged documents, mismatched information, incomplete KYC</p>
              </div>
              <div className={styles.redFlag}>
                <h5>Behavioral Indicators</h5>
                <p>Frequent profile changes, multiple rejected transactions</p>
              </div>
              <div className={styles.redFlag}>
                <h5>Operational Signs</h5>
                <p>Shared devices, inconsistent login patterns</p>
              </div>
            </div>
          </div>
        </section>

        {/* Data & Security */}
        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>6</span>
            Data Privacy & Security Measures
          </h2>

          <div className={styles.sectionContent}>
            <h3>6.1 Data Protection Framework</h3>
            
            <div className={styles.securityLayers}>
              <div className={styles.securityLayer}>
                <h4>Encryption & Storage</h4>
                <ul>
                  <li>AES-256 encryption at rest and in transit</li>
                  <li>Tokenization of sensitive data</li>
                  <li>Secure cloud infrastructure</li>
                  <li>Regular penetration testing</li>
                </ul>
              </div>

              <div className={styles.securityLayer}>
                <h4>Access Controls</h4>
                <ul>
                  <li>Role-based access permissions</li>
                  <li>Multi-factor authentication</li>
                  <li>Activity logging and audit trails</li>
                  <li>Privileged access management</li>
                </ul>
              </div>
            </div>

            <h3>6.2 Record Retention Policy</h3>
            <div className={styles.retentionTable}>
              <div className={styles.tableHeader}>
                <div>Record Type</div>
                <div>Retention Period</div>
                <div>Storage Format</div>
              </div>
              <div className={styles.tableRow}>
                <div>KYC Documents</div>
                <div>5 years post-account closure</div>
                <div>Encrypted digital copies</div>
              </div>
              <div className={styles.tableRow}>
                <div>Transaction Records</div>
                <div>7 years</div>
                <div>Database + encrypted backups</div>
              </div>
              <div className={styles.tableRow}>
                <div>Communication Logs</div>
                <div>3 years</div>
                <div>Secure log management system</div>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance & Governance */}
        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>7</span>
            Compliance Framework & Governance
          </h2>

          <div className={styles.sectionContent}>
            <h3>7.1 Organizational Structure</h3>
            
            <div className={styles.governanceStructure}>
              <div className={styles.governanceLevel}>
                <h4>Board of Directors</h4>
                <p>Ultimate responsibility for compliance framework</p>
              </div>
              
              <div className={styles.governanceLevel}>
                <h4>Risk & Compliance Committee</h4>
                <p>Quarterly reviews, policy approvals, oversight</p>
              </div>
              
              <div className={styles.governanceLevel}>
                <h4>Compliance Department</h4>
                <p>Daily operations, monitoring, reporting, training</p>
              </div>
            </div>

            <h3>7.2 Regulatory Framework Adherence</h3>
            <div className={styles.regulatoryList}>
              <div className={styles.regulatoryItem}>
                <h5>Reserve Bank of India (RBI)</h5>
                <p>Master Directions for P2P Platforms, KYC Guidelines</p>
              </div>
              <div className={styles.regulatoryItem}>
                <h5>Prevention of Money Laundering Act</h5>
                <p>PMLA 2002 & PML Rules 2005 compliance</p>
              </div>
              <div className={styles.regulatoryItem}>
                <h5>Information Technology Act</h5>
                <p>Data protection, electronic records compliance</p>
              </div>
            </div>
          </div>
        </section>

        {/* User Rights & Obligations */}
        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>8</span>
            User Rights, Obligations & Dispute Resolution
          </h2>

          <div className={styles.sectionContent}>
            <div className={styles.rightsObligationsGrid}>
              <div className={styles.rightsColumn}>
                <h3>8.1 User Rights</h3>
                <div className={styles.rightsList}>
                  <div className={styles.rightItem}>
                    <h5>Information Access</h5>
                    <p>Right to access personal data and KYC records</p>
                  </div>
                  <div className={styles.rightItem}>
                    <h5>Data Rectification</h5>
                    <p>Right to correct inaccurate personal information</p>
                  </div>
                  <div className={styles.rightItem}>
                    <h5>Account Deletion</h5>
                    <p>Right to request account closure and data deletion</p>
                  </div>
                </div>
              </div>

              <div className={styles.obligationsColumn}>
                <h3>8.2 User Obligations</h3>
                <div className={styles.obligationsList}>
                  <div className={styles.obligationItem}>
                    <h5>Truthful Information</h5>
                    <p>Provide accurate and complete information</p>
                  </div>
                  <div className={styles.obligationItem}>
                    <h5>Timely Updates</h5>
                    <p>Notify changes in contact or personal details</p>
                  </div>
                  <div className={styles.obligationItem}>
                    <h5>Cooperation</h5>
                    <p>Cooperate with verification and compliance checks</p>
                  </div>
                </div>
              </div>
            </div>

            <h3>8.3 Grievance Redressal Mechanism</h3>
            <div className={styles.grievanceProcess}>
              <div className={styles.processStep}>
                <h5>Step 1: Platform Escalation</h5>
                <p>Submit complaint via Help Center within platform</p>
                <span className={styles.timeline}>Response within 24 hours</span>
              </div>
              <div className={styles.processStep}>
                <h5>Step 2: Compliance Officer</h5>
                <p>Escalate to compliance team if unresolved</p>
                <span className={styles.timeline}>Resolution within 7 working days</span>
              </div>
              <div className={styles.processStep}>
                <h5>Step 3: Regulatory Authority</h5>
                <p>Approach appropriate regulatory authority</p>
              </div>
            </div>
          </div>
        </section>

        {/* Enforcement & Amendments */}
        <section className={styles.policySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>9</span>
            Policy Enforcement, Review & Amendments
          </h2>

          <div className={styles.sectionContent}>
            <h3>9.1 Non-Compliance Actions</h3>
            
            <div className={styles.enforcementActions}>
              <div className={styles.actionLevel}>
                <h5>Initial Warning</h5>
                <p>For minor or first-time compliance issues</p>
                <ul>
                  <li>Notification to user</li>
                  <li>Documentation required</li>
                  <li>7-day rectification period</li>
                </ul>
              </div>

              <div className={styles.actionLevel}>
                <h5>Temporary Restrictions</h5>
                <p>For repeated or moderate violations</p>
                <ul>
                  <li>Transaction limits imposed</li>
                  <li>Withdrawal restrictions</li>
                  <li>Enhanced verification required</li>
                </ul>
              </div>

              <div className={styles.actionLevel}>
                <h5>Account Termination</h5>
                <p>For severe or willful violations</p>
                <ul>
                  <li>Immediate platform access suspension</li>
                  <li>Outstanding transactions reviewed</li>
                  <li>Regulatory reporting if required</li>
                </ul>
              </div>
            </div>

            <h3>9.2 Amendment Process</h3>
            <div className={styles.amendmentProcess}>
              <div className={styles.amendmentStep}>
                <h5>Proposal & Review</h5>
                <p>Compliance department proposes changes based on regulatory updates</p>
              </div>
              <div className={styles.amendmentStep}>
                <h5>Committee Approval</h5>
                <p>Risk & Compliance Committee reviews and approves amendments</p>
              </div>
              <div className={styles.amendmentStep}>
                <h5>User Notification</h5>
                <p>Users notified via email and platform announcements 30 days before implementation</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default KycPolicy;